// src/pages/CheckoutPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import addressService from '../services/addressService';
import orderService from '../services/orderService';
import AddressCard from '../components/address/AddressCard';
import AddressForm from '../components/address/AddressForm';
import CartSummary from '../components/cart/CartSummary';
import Modal from '../components/common/Modal';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import { loadRazorpayScript, getErrorMessage } from '../utils/helpers';
import { PAYMENT_METHODS, RAZORPAY_CONFIG, FEATURES } from '../utils/constants';

/**
 * Checkout Page Component
 * 
 * Features:
 * - Select delivery address
 * - Add new address
 * - Choose payment method (Razorpay/COD)
 * - Place order
 * - Handle Razorpay payment
 */
const CheckoutPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { cart, clearCart } = useCart();
  
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.RAZORPAY);
  const [loading, setLoading] = useState(true);
  const [orderLoading, setOrderLoading] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  
  // Redirect if not authenticated or cart is empty
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!cart || !cart.items || cart.items.length === 0) {
      navigate('/cart');
      return;
    }
    
    fetchAddresses();
  }, [isAuthenticated, cart, navigate]);
  
  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const data = await addressService.getUserAddresses();
      setAddresses(data);
      
      // Auto-select default address
      const defaultAddress = data.find(addr => addr.isDefault);
      if (defaultAddress) {
        setSelectedAddressId(defaultAddress.id);
      } else if (data.length > 0) {
        setSelectedAddressId(data[0].id);
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddAddress = async (addressData) => {
    try {
      await addressService.createAddress(addressData);
      setShowAddressForm(false);
      fetchAddresses();
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };
  
  const handlePlaceOrder = async () => {
    if (!selectedAddressId) {
      alert('Please select a delivery address');
      return;
    }
    
    try {
      setOrderLoading(true);
      
      // Create order
      const orderData = {
        addressId: selectedAddressId,
        paymentMethod: paymentMethod,
        notes: '',
      };
      
      const response = await orderService.createOrder(orderData);
      
      // Handle Razorpay payment
      if (paymentMethod === PAYMENT_METHODS.RAZORPAY) {
        await handleRazorpayPayment(response);
      }
      // Handle COD - order already confirmed
      else {
        clearCart();
        navigate(`/orders/${response.id}`);
      }
    } catch (error) {
      alert(getErrorMessage(error));
    } finally {
      setOrderLoading(false);
    }
  };
  
  const handleRazorpayPayment = async (orderResponse) => {
  const loaded = await loadRazorpayScript();
  if (!loaded) {
    alert('Failed to load payment gateway. Please try again.');
    return;
  }
  
  const options = {
    key: RAZORPAY_CONFIG.KEY_ID,
    
    // FIX IS HERE: Convert your double/float rupees from backend into paise integers
    amount: Math.round(orderResponse.amount * 100), 
    
    currency: orderResponse.currency || 'INR',
    order_id: orderResponse.razorpayOrderId,
    name: 'College Drip',
    description: 'Order Payment',
      handler: async (response) => {
        // Payment successful - verify with backend
        try {
          await orderService.verifyPayment({
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature,
          });
          
          // Clear cart and redirect to order details
          clearCart();
          navigate('/orders');
        } catch (error) {
          alert('Payment verification failed. Please contact support.');
        }
      },
      prefill: {
        name: '',
        email: '',
        contact: '',
      },
      theme: {
        color: '#2563eb',
      },
    };
    
    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };
  
  if (loading) {
    return <Loading fullScreen message="Loading checkout..." />;
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
          <p className="text-gray-600 mt-2">
            Complete your order
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Address & Payment */}
          <div className="lg:col-span-2 space-y-8">
            {/* Delivery Address */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Delivery Address
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddressForm(true)}
                >
                  + Add New Address
                </Button>
              </div>
              
              {addresses.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">No addresses found</p>
                  <Button onClick={() => setShowAddressForm(true)}>
                    Add Delivery Address
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {addresses.map((address) => (
                    <AddressCard
                      key={address.id}
                      address={address}
                      selectable
                      selected={selectedAddressId === address.id}
                      onSelect={() => setSelectedAddressId(address.id)}
                      showActions={false}
                    />
                  ))}
                </div>
              )}
            </div>
            
            {/* Payment Method */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                Payment Method
              </h2>
              
              <div className="space-y-3">
                {/* Razorpay */}
                <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value={PAYMENT_METHODS.RAZORPAY}
                    checked={paymentMethod === PAYMENT_METHODS.RAZORPAY}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <div className="ml-3">
                    <p className="font-medium text-gray-900">
                      Online Payment
                    </p>
                    <p className="text-sm text-gray-600">
                      Pay using Card, UPI, or Wallet
                    </p>
                  </div>
                </label>
                
                {/* COD - controlled by FEATURES.COD_ENABLED flag in constants.js */}
                {FEATURES.COD_ENABLED && (
                  <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={PAYMENT_METHODS.COD}
                      checked={paymentMethod === PAYMENT_METHODS.COD}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <div className="ml-3">
                      <p className="font-medium text-gray-900">
                        Cash on Delivery
                      </p>
                      <p className="text-sm text-gray-600">
                        Pay when you receive
                      </p>
                    </div>
                  </label>
                )}
              </div>
            </div>
          </div>
          
          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <CartSummary
              cart={cart}
              onCheckout={handlePlaceOrder}
              checkoutLoading={orderLoading}
            />
          </div>
        </div>
      </div>
      
      {/* Add Address Modal */}
      <Modal
        isOpen={showAddressForm}
        onClose={() => setShowAddressForm(false)}
        title="Add Delivery Address"
        size="lg"
      >
        <AddressForm
          onSubmit={handleAddAddress}
          onCancel={() => setShowAddressForm(false)}
        />
      </Modal>
    </div>
  );
};

export default CheckoutPage;